/**
 * 문항 카드 AI 메뉴의 표시 정보 — 항목 순서·그룹·라벨·아이콘·노출 플래그.
 * 활성/비활성 판정은 여기 없다. 그것은 utils/questionCardAiActionSpecs 의 `eligibility` 몫이고,
 * 메뉴와 실행 직전 재검사가 같은 함수를 쓴다.
 */
import { TextLongLeftIcon } from '@zep-us/zus-icons';

import { BoltFillIcon, MessageDotsIcon, PenIcon } from '@/icons';

import { type BuildQuizListItem } from '../../../form/schema/BuildQuizListSchema';
import { type QuestionCardAiCopy } from '../hooks/useQuestionCardAiCopy';
import {
  type QuestionCardAiAction,
  type QuestionCardAiMenuItem,
} from '../types/questionCardAi.types';

import { HARDER_ENABLED } from './questionCardAi.const';

type MenuIcon = typeof PenIcon;
type MenuLabelKey = keyof QuestionCardAiCopy['menu'];

export type QuestionCardAiMenuEntry = {
  key: QuestionCardAiAction;
  group: QuestionCardAiMenuItem['group'];
  /** 라벨은 렌더 시점 로케일로 `copy.menu[labelKey]` 에서 찾는다. */
  labelKey: MenuLabelKey;
  /** 문항 유형별 라벨 override. 없으면 labelKey. */
  labelKeyByType?: Partial<Record<BuildQuizListItem['type'], MenuLabelKey>>;
  Icon: MenuIcon;
  /** false 면 메뉴에서 숨긴다(플래그). 기본 노출. */
  visible?: boolean;
  /**
   * 자격 미달이면 비활성+사유 대신 항목 자체를 뺀다. 기본은 PRD §7-2 대로 흐리게 + 사유 툴팁.
   * 사유가 `unsupportedType`(지원하지 않는 문항 유형)이면 이 플래그와 무관하게 항상 뺀다.
   * 잠금(다른 AI 작업 진행 중)은 자격이 아니라 일시 상태라 이 플래그와 무관하게 비활성으로 남는다.
   */
  hideWhenIneligible?: boolean;
};

/** 렌더 순서 = 배열 순서. 그룹 사이 구분은 QUESTION_CARD_AI_MENU_GROUP_ORDER 가 정한다. */
export const QUESTION_CARD_AI_MENU_ENTRIES: readonly QuestionCardAiMenuEntry[] =
  [
    {
      key: 'similar',
      group: 'generate',
      labelKey: 'similar',
      Icon: PenIcon,
    },
    {
      key: 'harder',
      group: 'generate',
      labelKey: 'harder',
      Icon: BoltFillIcon,
      visible: HARDER_ENABLED,
    },
    {
      key: 'fill_options',
      group: 'edit',
      labelKey: 'fillOptions',
      labelKeyByType: { subjective: 'fillAnswers' },
      Icon: TextLongLeftIcon,
      // 선지가 없는 유형·빈 슬롯 없음 등은 "할 수 없는 일" 이라 안내할 가치가 없다 — 항목을 뺀다.
      hideWhenIneligible: true,
    },
    {
      key: 'chat',
      group: 'chat',
      labelKey: 'chat',
      Icon: MessageDotsIcon,
    },
  ];

export const QUESTION_CARD_AI_MENU_GROUP_ORDER: readonly QuestionCardAiMenuItem['group'][] =
  ['generate', 'edit', 'chat'];
